/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getDefaultMeetingNoLength } from '../config';
import { JOIN_MEETING_NO_HISTORY_MAX, STORAGE_JOIN_DISPLAY_NAME, STORAGE_JOIN_MEETING_NO_HISTORY, STORAGE_REMEMBER_JOIN_NAME } from '../constants';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
function ChevronIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M6 9l6 6 6-6', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })));
}
function readRememberFlag() {
    try {
        return window.localStorage.getItem(STORAGE_REMEMBER_JOIN_NAME) === '1';
    }
    catch {
        return false;
    }
}
function readSavedName() {
    try {
        return window.localStorage.getItem(STORAGE_JOIN_DISPLAY_NAME) || '';
    }
    catch {
        return '';
    }
}
function persistName(rememberName, displayName) {
    try {
        window.localStorage.setItem(STORAGE_REMEMBER_JOIN_NAME, rememberName ? '1' : '0');
        if (rememberName) {
            window.localStorage.setItem(STORAGE_JOIN_DISPLAY_NAME, displayName.trim());
        }
        else {
            window.localStorage.removeItem(STORAGE_JOIN_DISPLAY_NAME);
        }
    }
    catch (error) {
        logger.warn(`记住入会名称写入失败 | ${error?.message || error}`);
    }
}
function readMeetingNoHistory() {
    try {
        const raw = window.localStorage.getItem(STORAGE_JOIN_MEETING_NO_HISTORY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.map(item => String(item).replace(/\D/g, '')).filter(Boolean)
            : [];
    }
    catch {
        return [];
    }
}
function persistMeetingNoHistory(meetingNo) {
    const next = [meetingNo, ...readMeetingNoHistory().filter(item => item !== meetingNo)]
        .slice(0, JOIN_MEETING_NO_HISTORY_MAX);
    try {
        window.localStorage.setItem(STORAGE_JOIN_MEETING_NO_HISTORY, JSON.stringify(next));
    }
    catch (error) {
        logger.warn(`会议号历史写入失败 | ${error?.message || error}`);
    }
}
/**
 * 加入会议表单：手机全屏、桌面弹层共用字段。
 *
 * @param {IProps} props - Props.
 * @returns {React.ReactElement}
 */
export default function JoinMeetingForm({ busy, defaultDisplayName, error, isMobile, onClose, onJoin, recentMeetingNos }) {
    const rememberInitial = readRememberFlag();
    const [meetingNo, setMeetingNo] = useState('');
    const [displayName, setDisplayName] = useState(() => (rememberInitial ? readSavedName() : '') || defaultDisplayName || '');
    const [rememberName, setRememberName] = useState(rememberInitial);
    const [micOn, setMicOn] = useState(true);
    const [speakerOn, setSpeakerOn] = useState(true);
    const [videoOn, setVideoOn] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const comboboxRef = useRef(null);
    const meetingNoLength = getDefaultMeetingNoLength();
    const digits = useMemo(() => meetingNo.replace(/\D/g, ''), [meetingNo]);
    const canSubmit = digits.length === meetingNoLength && !busy;
    const meetingNoOptions = useMemo(() => {
        const seen = new Set();
        const options = [];
        [...readMeetingNoHistory(), ...recentMeetingNos].forEach(item => {
            const meetingNoItem = String(item || '').replace(/\D/g, '');
            if (meetingNoItem && !seen.has(meetingNoItem)) {
                seen.add(meetingNoItem);
                options.push(meetingNoItem);
            }
        });
        return options;
    }, [recentMeetingNos]);
    useEffect(() => {
        if (!historyOpen) {
            return undefined;
        }
        const onMouseDown = (event) => {
            const root = comboboxRef.current;
            if (root && event.target instanceof Node && !root.contains(event.target)) {
                setHistoryOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [historyOpen]);
    const onSubmit = (event) => {
        event.preventDefault();
        if (digits.length < meetingNoLength) {
            return;
        }
        persistName(rememberName, displayName);
        persistMeetingNoHistory(digits);
        logger.info(`[${new Date().toISOString()}] 提交加入会议表单 | meetingNo=${digits}`
            + ` micOn=${micOn} speakerOn=${speakerOn} videoOn=${videoOn}`);
        onJoin({
            displayName: displayName.trim(),
            meetingNo: digits,
            startSilent: !speakerOn,
            startWithAudioMuted: !micOn,
            startWithVideoMuted: !videoOn
        });
    };
    const form = (React.createElement("form", { className: isMobile ? 'portal-join-form' : 'portal-join-form is-desktop', onClick: event => event.stopPropagation(), onSubmit: onSubmit },
        React.createElement("header", { className: 'portal-join-header' },
            React.createElement("button", { className: 'portal-join-cancel', onClick: onClose, type: 'button' }, M.CANCEL),
            React.createElement("h1", null, M.JOIN_MEETING),
            React.createElement("span", null)),
        React.createElement("div", { className: 'portal-join-rows' },
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.MEETING_NO),
                React.createElement("div", { className: historyOpen ? 'portal-join-combobox is-open' : 'portal-join-combobox', ref: comboboxRef },
                    React.createElement("div", { className: 'portal-join-combobox-field' },
                        React.createElement("input", { "aria-expanded": historyOpen, "aria-haspopup": 'listbox', autoFocus: true, disabled: busy, onChange: event => {
                                setMeetingNo(event.target.value.replace(/\D/g, '').slice(0, meetingNoLength));
                            }, placeholder: M.MEETING_NO_PLACEHOLDER, role: 'combobox', value: meetingNo }),
                        React.createElement("button", { "aria-label": M.HISTORY, className: 'portal-join-combobox-toggle', disabled: busy || meetingNoOptions.length === 0, onClick: () => {
                                if (!meetingNoOptions.length) {
                                    return;
                                }
                                setHistoryOpen(open => !open);
                            }, type: 'button' },
                            React.createElement(ChevronIcon, null))),
                    historyOpen && meetingNoOptions.length > 0 ? (React.createElement("ul", { className: 'portal-join-combobox-list', role: 'listbox' }, meetingNoOptions.map(item => (React.createElement("li", { key: item },
                        React.createElement("button", { className: item === meetingNo ? 'is-active' : undefined, onClick: () => {
                                setMeetingNo(item);
                                setHistoryOpen(false);
                            }, role: 'option', type: 'button' }, item)))))) : null)),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.YOUR_NAME),
                React.createElement("div", { className: 'portal-join-control' },
                    React.createElement("input", { disabled: busy, onChange: event => setDisplayName(event.target.value), value: displayName }))),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label is-muted' }, M.REMEMBER_JOIN_NAME),
                React.createElement("button", { "aria-checked": rememberName, className: rememberName ? 'portal-join-check is-on' : 'portal-join-check', onClick: () => setRememberName(value => !value), role: 'checkbox', type: 'button' },
                    React.createElement("span", null))),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.ENABLE_MIC),
                React.createElement("button", { "aria-checked": micOn, className: micOn ? 'portal-join-switch is-on' : 'portal-join-switch', onClick: () => setMicOn(value => !value), role: 'switch', type: 'button' })),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.ENABLE_SPEAKER),
                React.createElement("button", { "aria-checked": speakerOn, className: speakerOn ? 'portal-join-switch is-on' : 'portal-join-switch', onClick: () => setSpeakerOn(value => !value), role: 'switch', type: 'button' })),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.ENABLE_VIDEO),
                React.createElement("button", { "aria-checked": videoOn, className: videoOn ? 'portal-join-switch is-on' : 'portal-join-switch', onClick: () => setVideoOn(value => !value), role: 'switch', type: 'button' }))),
        error ? React.createElement("div", { className: 'portal-error inline' }, error) : null,
        React.createElement("button", { className: 'portal-join-submit', disabled: !canSubmit, type: 'submit' }, M.CONFIRM_JOIN)));
    if (isMobile) {
        return (React.createElement("div", { className: 'portal-join-page' }, form));
    }
    return (React.createElement("div", { className: 'portal-modal-backdrop portal-join-desktop-backdrop', onClick: onClose, role: 'presentation' }, form));
}
